import { useState, useRef, cloneElement, isValidElement } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingArrow,
} from '@floating-ui/react';

/**
 * Lightweight tooltip wrapping Floating UI. Replaces native `title=` for
 * places where the OS-styled rendering + 1s default delay clash with the
 * Live Map's dark-glass aesthetic.
 *
 * Props:
 *  - content    : ReactNode  what the tooltip shows (multi-line via JSX)
 *  - children   : ReactNode  the trigger; can be an element or a string
 *  - placement  : string     'top' | 'bottom' | 'left' | 'right' (+ start/end), default 'top'
 *  - delay      : number     ms before opening on hover (default 150)
 *  - disabled   : boolean    skip the tooltip entirely (passes children through)
 *  - className  : string     extra classes for the floating panel
 */
export default function Tooltip({
  content,
  children,
  placement = 'top',
  delay = 150,
  disabled = false,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(6), flip(), shift({ padding: 8 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { move: false, delay: { open: delay, close: 0 } });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  if (disabled || content == null || content === '') return children;

  // Allow string or element children. Strings get wrapped in a span so we
  // can attach the ref + handlers.
  const trigger = isValidElement(children) ? (
    cloneElement(children, { ref: refs.setReference, ...getReferenceProps(children.props) })
  ) : (
    <span ref={refs.setReference} {...getReferenceProps()}>
      {children}
    </span>
  );

  return (
    <>
      {trigger}
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              background: 'rgba(15,23,42,0.94)',
              backdropFilter: 'blur(12px) saturate(160%)',
              WebkitBackdropFilter: 'blur(12px) saturate(160%)',
              color: '#f1f5f9',
              fontSize: '11px',
              lineHeight: 1.4,
              padding: '6px 9px',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
              maxWidth: 260,
              zIndex: 9999,
              pointerEvents: 'none',
            }}
            className={className}
            {...getFloatingProps()}
          >
            {content}
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill="rgba(15,23,42,0.94)"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth={1}
              width={10}
              height={5}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
